import _ from '../../utils/underscore';
import Store from '../../utils/store';
import ServiceFactory from '../service_factory';
import StopWatch from '../../utils/stop_watch';
import BaseObject from '../../base/base_object';
import {IElasticEventData} from '../../../../common/interfaces/structured-logging-service-interface';

export function convertToCents(value: number) {
    const result = Math.floor(value * 100 + .001);
    return result;
}

export function isValidJackpotValue(jpValue: any) {
    if (!_.isNumber(jpValue)) {
        return false;
    }
    const value = jpValue as number;
    if (value <= 0) {
        return false;
    }
    return true;
}

const STORAGE_KEY_PREFIX = 'BlueRibbon';

function composeBlueRibbonLocalStorageKey(keyName: string) {
    return `${STORAGE_KEY_PREFIX}_${keyName}`;
}

export function setBlueRibbonLocalStorageKey(keyName: string, value: string): void {
    Store.set(composeBlueRibbonLocalStorageKey(keyName), value);
}

export function deleteBlueRibbonLocalStorageKey(keyName: string): void {
    Store.del(composeBlueRibbonLocalStorageKey(keyName));
}

export function getBlueRibbonLocalStorageKey(keyName: string): string {
    return Store.get(composeBlueRibbonLocalStorageKey(keyName));
}

export function clearBlueRibbonLocalStorageKey(keyName: string): void {
     Store.del(composeBlueRibbonLocalStorageKey(keyName));
}

export function formatBlueRibbonLogMessage(message: string): string {
    return `BlueRibbon: ${message}`;
}

interface BlueRibbonOperationExecutionMonitorOptions {
    operationName: string;
    operationParams?: any;
    logExecutionTimeToElastic?: boolean;
}

export class BlueRibbonExecutionMonitor extends BaseObject {
    static CLASS = "BlueRibbonExecutionMonitor";

    constructor(private _serviceFactory: ServiceFactory) {
        super();
    }

    execute<TResult>(options: BlueRibbonOperationExecutionMonitorOptions, operationToExecute: () => Promise<TResult>): Promise<TResult> {
        const stopWatch = new StopWatch(true);
        const operationId =  Date.now();

        const operationExecutionTimer = setTimeout(() => {
            this.logger.warn(`[${operationId}] Operation ${options.operationName} still not finished after ${stopWatch.getElapsedTime().toString()}`)
            this._sendElasticError(options.operationName, 'long_running_operation');
        }, 30000);
        let params: any = [];
        if(options.operationParams) {
            params = ['with params', options.operationParams];
        }
        this.logger.info(`[${operationId}]Begin ${options.operationName}`, ...params);

        return operationToExecute().then(data => {
            clearTimeout(operationExecutionTimer);
            this.logger.info(`[${operationId}] Finished ${options.operationName} after ${stopWatch.stop().toString()} with response`, data);
            if(options.logExecutionTimeToElastic) {
                this._sendElasticInfo(options.operationName, 'execution_time', stopWatch.getElapsedTime().totalSeconds);
            }

            return data;
        }).catch(err => {
            clearTimeout(operationExecutionTimer);
            this.logger.info(`[${operationId}] Failed ${options.operationName} after ${stopWatch.stop().toString()};`, err);
            this._sendElasticError(options.operationName, err, stopWatch.getElapsedTime().totalSeconds);
            throw err;
        });
    }

    executeSync<TResult>(options: BlueRibbonOperationExecutionMonitorOptions, operationToExecute: () => TResult): TResult {
        const stopWatch = new StopWatch(true);
        const operationId =  Date.now();

        const operationExecutionTimer = setTimeout(() => {
            this.logger.warn(`[${operationId}] Operation ${options.operationName} still not finished after ${stopWatch.getElapsedTime().toString()}`)
            this._sendElasticError(options.operationName, 'long_running_operation');
        }, 30000);
        let params: any = [];
        if(options.operationParams) {
            params = ['with params', options.operationParams];
        }
        this.logger.info(`[${operationId}]Begin ${options.operationName}`, ...params);

        let data: TResult;
        try {
            data = operationToExecute();
        }
        catch(err) {
            clearTimeout(operationExecutionTimer);
            this.logger.info(`[${operationId}] Failed ${options.operationName} after ${stopWatch.stop().toString()};`, err);
            this._sendElasticError(options.operationName, err, stopWatch.getElapsedTime().totalSeconds);
            throw err;
        }

        clearTimeout(operationExecutionTimer);
        this.logger.info(`[${operationId}] Finished ${options.operationName} after ${stopWatch.stop().toString()} with response`, data);
        if(options.logExecutionTimeToElastic) {
            this._sendElasticInfo(options.operationName, 'execution_time', stopWatch.getElapsedTime().totalSeconds);
        }

        return data;
    }

    eventReceived(eventName: string, data: any): void {
        this.logger.info(`Received event ${eventName} with params`, data);
    }

    info(...logData) {
        this.logger.info(...logData);
    }

    error(...logData) {
        this.logger.error(...logData);
    }

    private _sendElasticInfo(action: string, label: string, value?: any) {
        try {
            const data: IElasticEventData = this._composeElasticLog(action, label, value);
            this._serviceFactory.structuredLoggingService().getLogger(data).info();
        } catch(ex) {
            this.logger.error(`BlueRibbon ${action} - sendElasticInfo exception`, ex);
        }
    }

    private _sendElasticError(action: string, label: string, value?: any) {
        try {
            const data: IElasticEventData = this._composeElasticLog(action, label, value);
            this._serviceFactory.structuredLoggingService().getLogger(data).error();
        } catch(ex) {
            this.logger.error(`BlueRibbon ${action} - sendElasticError exception`, ex);
        }
    }

    private _composeElasticLog(action: string, label: string, value: any): IElasticEventData {
        const data: IElasticEventData = {
            CGPEventCategory: 'Blue_Ribbon',
            CGPEventAction: action
        };
        if (label) {
            data.CGPEventLabel = label;
        }

        if (!_.isNullOrUndefined(value)) {
            const valueAsNumber = Number(value);
            if (_.isNumber(valueAsNumber)) {
                data.CGPEventValueAsNumber = valueAsNumber;
            } else {
                data.CGPEventValue = value;
            }
        }
        return data;
    }
}